import React, { useState, useMemo, useEffect } from 'react';
import { TLanguageIDs, IMenuTranslation, IDefaultLanguage, IGetCustomerLanguages, ICustomerLanguages } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import Text from 'components/Text/Text';
import LanguageBar from './components/LanguageBar';
import { NameTranslationField } from './components/NameTranslationField';
import { DescriptionTranslationField } from './components/DescriptionTranslationField';
import { getCustomerLanguages } from 'backend/customers';
import classNames from 'classnames';
import style from './MenuTranslationForm.module.scss';

interface IProps {
	setFetchedTranslations?: (value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>) => void;
	setDefaultCustomerLanguage?: (value: Nullable<IDefaultLanguage>) => void;
	setTranslationTab: (value: Nullable<TLanguageIDs>) => void;
	changeNameTranslation?: (languageId: TLanguageIDs, value: string) => void;
	changeDescriptionTranslation?: (languageId: TLanguageIDs, value: string) => void;
	isVisible?: boolean;
	isShowLanguageName?: boolean;
	IDSelectedCustomer: Nullable<string>;
	translations: IMenuTranslation[];
	selectedTranslationTab: Nullable<TLanguageIDs>;
	className?: string;
	nameLabel?: string;
	hasDescriptionFullInfoField?: boolean;
}

export const MenuTranslationForm: React.FC<IProps> = React.memo(
	({
		setFetchedTranslations,
		setDefaultCustomerLanguage,
		setTranslationTab,
		changeNameTranslation,
		changeDescriptionTranslation,
		isVisible = true,
		isShowLanguageName = true,
		IDSelectedCustomer,
		translations,
		selectedTranslationTab,
		className,
		nameLabel,
		hasDescriptionFullInfoField = false,
	}) => {
		const [fetchedLanguages, setFetchedLanguages] = useState<ICustomerLanguages[]>([]);
		const { response, refetch } = useFetchData<IGetCustomerLanguages, string>(
			IDSelectedCustomer ? getCustomerLanguages : null,
			IDSelectedCustomer || '',
		);
		const containerClasses = classNames(style.container, { [style.visible]: isVisible }, className);
		const languageName = fetchedLanguages.find((el) => el.id === selectedTranslationTab)?.name || null;
		const languageIDs = useMemo(() => fetchedLanguages.map((el) => el.id), [fetchedLanguages]);
		const defaultLanguage = useMemo(
			() => response?.data.languages.find((language) => language.isDefault),
			[response],
		);

		useEffect(() => {
			refetch();
		}, [IDSelectedCustomer]);

		useEffect(() => {
			if (response) {
				setFetchedLanguages(response.data.languages);

				const languageIDs = response.data.languages.map((language) => language.id);
				const defaultCustomerLanguage = defaultLanguage
					? {
							id: defaultLanguage.id,
							name: defaultLanguage.name,
					  }
					: null;

				setFetchedTranslations && setFetchedTranslations(languageIDs, defaultCustomerLanguage);
				setDefaultCustomerLanguage && setDefaultCustomerLanguage(defaultCustomerLanguage);
			}
		}, [response]);

		return (
			<div className={containerClasses} data-test="MenuTranslationForm">
				{translations.length ? (
					<>
						<LanguageBar
							selectedTranslationTab={selectedTranslationTab}
							languageIDs={languageIDs}
							translations={translations}
							defaultLanguage={defaultLanguage}
							setTranslationTab={setTranslationTab}
						/>
						{isShowLanguageName && (
							<Text
								translationNameSpace="form"
								translationKey={languageName}
								theme="secondary"
								size="l"
								weight="bold"
							/>
						)}
						{translations.map(
							(el) =>
								el.languageId === selectedTranslationTab && (
									<React.Fragment key={el.languageId}>
										<NameTranslationField
											languageId={el.languageId}
											translations={translations}
											nameLabel={nameLabel}
											changeNameTranslation={changeNameTranslation}
										/>
										<DescriptionTranslationField
											languageId={el.languageId}
											translations={translations}
											changeDescriptionTranslation={changeDescriptionTranslation}
											hasDescriptionFullInfoField={hasDescriptionFullInfoField}
										/>
									</React.Fragment>
								),
						)}
					</>
				) : (
					<>
						{isShowLanguageName ? (
							<Text
								translationNameSpace="form"
								translationKey="noSelectedLanguagesHeading"
								theme="secondary"
								size="l"
								weight="bold"
							/>
						) : (
							<Text translationNameSpace="form" translationKey="noSelectedLanguagesHeading" />
						)}
					</>
				)}
			</div>
		);
	},
);
